/**
 * GA4 e-ticaret yardımcıları.
 *
 * Repodaki 6 mevcut `dataLayer` push'u eski Universal Analytics Enhanced
 * Ecommerce şemasını (`ecommerce.add.products[]`, `dimension9..52`) kullanıyor.
 * Yeni event'ler (`view_item`, `view_item_list`, `select_item`) GA4'ün
 * `ecommerce.items[]` şemasıyla yazılır; iki şema bir süre yan yana yaşayacağı
 * için yeni alan üretimi burada tek yerde toplanıyor.
 */

/** Tüm GA4 event'lerinde sabit para birimi (site yalnız TRY ile satış yapıyor). */
export const GA4_CURRENCY = 'TRY';

/**
 * GA4 `items[]` dizisinin tek bir elemanı.
 *
 * Alan adları GA4 sözleşmesindeki snake_case ile birebir aynı olmak zorunda;
 * bu yüzden repo genelindeki camelCase kuralı burada bilinçli olarak uygulanmaz.
 */
export interface Ga4Item {
  item_id: string;
  item_name: string;
  item_brand?: string;
  item_category?: string;
  item_category2?: string;
  item_variant?: string;
  item_list_id?: string;
  item_list_name?: string;
  index?: number;
  price?: number;
  quantity?: number;
  currency?: string;
}

/**
 * `item_id` üretir: `<eventCode>-<yyyyMMdd>`.
 *
 * Reçete, mevcut UA push'larındaki `createProductsDataLayer()` ile aynı
 * (`performance-ticket.service.ts`), böylece iki şema aynı ürünü aynı kimlikle
 * raporlar. Performans tarihi bilinmiyorsa (etkinlik seviyesindeki listeler)
 * yalnız `eventCode`'a düşer — GA4 tarafında etkinlik hâlâ tekil kalır.
 */
export function buildGa4ItemId(eventCode?: string | null, performanceDate?: Date | string | null): string {
  const code = (eventCode ?? '').trim();
  const datePart = formatYyyyMmDd(performanceDate);
  return datePart ? `${code}-${datePart}` : code;
}

/**
 * Tarihi yerel tarih parçalarından `yyyyMMdd`'ye çevirir.
 *
 * `toISOString()` kullanılmaz: UTC'ye çevirdiği için Türkiye saatiyle akşam
 * başlayan bir performansın tarihini bir gün kaydırabilir.
 */
function formatYyyyMmDd(value?: Date | string | null): string {
  if (!value) {
    return '';
  }

  const date = value instanceof Date ? value : new Date(String(value).replace(' ', 'T'));
  if (isNaN(date.getTime())) {
    return '';
  }

  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}${month}${day}`;
}

/**
 * Formatlı fiyat metnini GA4'ün beklediği sayıya çevirir.
 *
 * Liste ve detay view-model'lerinde fiyat çoğunlukla sayı değil metindir
 * (`minPrice`, `priceInfo`, `EventDetail.price`) ve "1.500,00 TL" gibi Türkçe
 * biçimde, bazen de "150 TL - 400 TL" gibi aralık olarak gelir; aralıkta ilk
 * (en düşük) değer alınır.
 *
 * `FactoryUtils.formatAmountDataLayer` bilinçli olarak kullanılmıyor: naif
 * `amount % 100` yüzünden 1005 kuruşu "10.5" olarak üretiyor (mevcut hata).
 * Kuruş cinsinden sayısal alanlar için `centsToGa4Price` kullanılır.
 */
export function parseGa4Price(raw?: string | number | null): number | undefined {
  if (raw === null || raw === undefined || raw === '') {
    return undefined;
  }

  if (typeof raw === 'number') {
    return isFinite(raw) ? raw : undefined;
  }

  // Aralıkların ilk parçası: "150,00 TL - 400,00 TL" → "150,00 TL"
  const firstPart = raw.split(/[-–]/)[0] ?? '';
  const numeric = firstPart.replace(/[^\d.,]/g, '');
  if (!numeric) {
    return undefined;
  }

  const normalized = normalizeTurkishNumber(numeric);
  const parsed = Number(normalized);
  return isFinite(parsed) ? parsed : undefined;
}

/**
 * Türkçe biçimli sayıyı (binlik `.`, ondalık `,`) `Number` girdisine çevirir.
 *
 * Yalnız nokta içeren durum belirsizdir: "1.500" binlik, "1.50" ondalıktır.
 * Ayırt etme kuralı, son noktadan sonraki basamak sayısıdır — üç basamak
 * binlik ayırıcı kabul edilir.
 */
function normalizeTurkishNumber(numeric: string): string {
  const hasComma = numeric.includes(',');
  const hasDot = numeric.includes('.');

  if (hasComma) {
    // Virgül varsa ondalık ayırıcı odur; noktalar binliktir.
    return numeric.replace(/\./g, '').replace(',', '.');
  }

  if (hasDot) {
    const decimals = numeric.length - numeric.lastIndexOf('.') - 1;
    return decimals === 3 ? numeric.replace(/\./g, '') : numeric;
  }

  return numeric;
}

/** Kuruş cinsinden sayısal tutarı GA4 fiyatına (TL) çevirir. */
export function centsToGa4Price(amountInCents?: number | null): number | undefined {
  if (amountInCents === null || amountInCents === undefined || !isFinite(amountInCents)) {
    return undefined;
  }
  return Math.round(amountInCents) / 100;
}

/**
 * Liste satırlarının `minPrice`/`priceInfo` çiftinden TL cinsinden fiyat üretir.
 *
 * İki alan AYNI ÖLÇEKTE DEĞİL ve bunu şablonlar belgeliyor: `minPrice` her yerde
 * `customCurrency` süzgecinden geçiriliyor (`custom-currency.pipe.ts`,
 * `formatTurkishLira` → `değer / 100`), yani KURUŞ; `priceInfo` ise doğrudan
 * basılıyor ("150,00 TL - 400,00 TL" gibi), yani zaten biçimlenmiş METİN.
 * Modelde `minPrice: string` yazsa da API sayı gönderiyor (ör. 250000 = 2.500 TL).
 *
 * Bu ikisini ayırmadan tek bir ayrıştırıcıya vermek fiyatı 100 katına çıkarır.
 */
export function resolveListItemPrice(
  minPrice?: string | number | null,
  priceInfo?: string | number | null,
): number | undefined {
  const cents = toCentsNumber(minPrice);
  // Ayırıcısız tam sayı → kuruş. Aksi halde `minPrice` beklenmedik biçimde
  // metin gelmiş demektir; o zaman kuruş varsayımı yapılmaz, metin ayrıştırılır.
  const fromMinPrice =
    cents === undefined ? parseGa4Price(minPrice) : centsToGa4Price(cents);

  if (fromMinPrice !== undefined && fromMinPrice > 0) {
    return fromMinPrice;
  }
  return parseGa4Price(priceInfo);
}

/**
 * `minPrice`'ı kuruş sayısına çevirir; yalnız ayırıcı içermeyen tam sayılar
 * kabul edilir. Biçimlenmiş bir metin ("2.500,00 TL") gelirse `undefined` döner
 * ve çağıran `priceInfo` yoluna düşer — böylece iki ölçek karışmaz.
 */
function toCentsNumber(minPrice?: string | number | null): number | undefined {
  if (minPrice === null || minPrice === undefined || minPrice === '') {
    return undefined;
  }
  if (typeof minPrice === 'number') {
    return isFinite(minPrice) ? minPrice : undefined;
  }
  return /^\d+$/.test(minPrice.trim()) ? Number(minPrice.trim()) : undefined;
}

/**
 * `rock$MUSIC` biçimindeki alt kategori kodundan okunabilir alt kategori üretir.
 * Mevcut UA push'ları da aynı ayrıştırmayı yapıyor (`dimension12`).
 */
export function ga4SubCategory(subCategoryCode?: string | null): string | undefined {
  const code = (subCategoryCode ?? '').split('$')[0];
  return code ? code : undefined;
}

/**
 * Bir GA4 item'ı üretmek için bileşenlerin sağlaması gereken alanlar.
 *
 * Sayfa ve liste view-model'leri birbirinden farklı (bazısında `minPrice`,
 * bazısında `priceInfo`, bazısında kuruş cinsinden sayısal alan var), bu yüzden
 * eşleme bu ara tipte toplanıyor — alan seçimi çağıran bileşende, GA4 şeması
 * burada kalır.
 */
export interface Ga4EventItemSource {
  eventCode?: string | null;
  eventName?: string | null;
  performanceDate?: Date | string | null;
  /** Formatlı metin ya da sayı; kuruş için önce `centsToGa4Price` uygulanmalı. */
  price?: string | number | null;
  category?: string | null;
  subCategoryCode?: string | null;
  venueName?: string | null;
  /** Sanatçı adı; yoksa organizatör adı (UA'daki `dimension9`'un karşılığı). */
  brand?: string | null;
  listId?: string;
  listName?: string;
  index?: number;
}

/**
 * Kaynak alanlarını GA4 `items[]` elemanına çevirir.
 *
 * Boş alanlar hiç yazılmaz: GA4 tarafında `undefined` bir alan, boş string'e
 * göre daha temiz raporlanır. Mekan adı `item_variant`'a konur — GA4'ün hazır
 * bir mekan alanı yok ve GTM tarafındaki mevcut UA eşlemesi (`dimension10`)
 * de aynı bilgiyi taşıyor.
 */
export function buildGa4EventItem(source: Ga4EventItemSource): Ga4Item {
  const item: Ga4Item = {
    item_id: buildGa4ItemId(source.eventCode, source.performanceDate),
    item_name: source.eventName ?? '',
    currency: GA4_CURRENCY,
  };

  const brand = source.brand ?? undefined;
  const category = source.category ?? undefined;
  const subCategory = ga4SubCategory(source.subCategoryCode);
  const venueName = source.venueName ?? undefined;
  const price = parseGa4Price(source.price);

  if (brand) item.item_brand = brand;
  if (category) item.item_category = category;
  if (subCategory) item.item_category2 = subCategory;
  if (venueName) item.item_variant = venueName;
  if (price !== undefined) item.price = price;
  if (source.listId) item.item_list_id = source.listId;
  if (source.listName) item.item_list_name = source.listName;
  if (source.index !== undefined) item.index = source.index;

  return item;
}
